/**
 * Paste transform utilities for cleaning Word/Office HTML and
 * reconstructing structure from PDF/plain-text clipboard content.
 *
 * Wired into TipTap via editorProps.transformPastedHTML and
 * editorProps.clipboardTextSerializer / transformPastedText.
 */

// ── Allowed heading levels (must stay in sync with extensions.ts) ──────────
const ALLOWED_HEADING_LEVELS = [2, 3] as const;
const MAX_HEADING = Math.max(...ALLOWED_HEADING_LEVELS);
const MIN_HEADING = Math.min(...ALLOWED_HEADING_LEVELS);

// ── Word / Office HTML sanitizer ───────────────────────────────────────────

/**
 * Strips Office/Word artefacts and normalizes HTML so TipTap's schema
 * matcher can consume the paste payload cleanly.
 */
export function transformPastedHTML(html: string): string {
  let result = html;

  // 1. Strip conditional comments  <!--[if ...]>...<![endif]-->
  result = result.replace(/<!--\[if[^]*?<!\[endif\]-->/gi, '');
  // Strip normal HTML comments too
  result = result.replace(/<!--.*?-->/gs, '');

  // 2. Remove Office XML namespace tags: <o:p>, <w:*>, <m:*>, <v:*>
  result = result.replace(/<\/?[owmv]:[^>]*>/gi, '');

  // 3. Remove xmlns:* namespace attributes
  result = result.replace(/\s+xmlns:[a-z]+=["'][^"']*["']/gi, '');

  // 4. Convert MsoListParagraph runs to real list items BEFORE stripping
  //    Mso classes, since the conversion depends on MsoListParagraph class.
  result = convertMsoListsToHTML(result);

  // 5. Remove class="Mso*" attributes
  result = result.replace(/\s+class=["']Mso[^"']*["']/gi, '');

  // 6. Strip mso-* style declarations (keep other style props)
  result = result.replace(/\s*mso-[^;:"']+:[^;:"']+;?/gi, '');

  // 7. Remove empty style attributes left behind
  result = result.replace(/\s+style=["']\s*["']/gi, '');

  // 8. Down-map heading levels outside the allowed range
  result = downMapHeadings(result);

  // 9. Strip <font> wrappers, keeping children
  result = result.replace(/<\/?font[^>]*>/gi, '');

  // 10. Strip empty <span> wrappers (no attributes or only empty style)
  result = result.replace(/<span(?:\s+style=["']\s*["'])?\s*>(.*?)<\/span>/gi, '$1');

  // 11. Normalise smart quotes and non-breaking spaces
  result = normaliseTypography(result);

  // 12. Clean remaining empty paragraphs that Word inserts as spacers
  result = result.replace(/<p[^>]*>\s*(&nbsp;|\u00A0)?\s*<\/p>/gi, '');

  return result;
}

// ── PDF / plain-text paragraph reconstructor ───────────────────────────────

/**
 * Converts plain text (typically from a PDF copy) into structured HTML.
 * - Blank-line-separated blocks → `<p>` elements
 * - Single `\n` within a block → space (PDF soft-wraps)
 * - Lines starting with bullet glyphs → `<ul><li>`
 * - Lines starting with `1.` / `1)` patterns → `<ol><li>`
 */
export function transformPastedText(text: string): string {
  if (!text.trim()) {return '';}

  // Split on one or more blank lines
  const blocks = text.split(/\n\s*\n/);
  const htmlParts: string[] = [];

  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) {continue;}

    const lines = trimmed.split('\n').map((l) => l.trim()).filter(Boolean);

    // Detect bullet list block: every line starts with a bullet glyph
    if (lines.length > 0 && lines.every((l) => /^[\u2022\u2023\u25E6\u2043\u2219•\-*]\s/.test(l))) {
      const items = lines.map((l) => `<li>${escapeHtml(l.replace(/^[\u2022\u2023\u25E6\u2043\u2219•\-*]\s+/, ''))}</li>`);
      htmlParts.push(`<ul>${items.join('')}</ul>`);
      continue;
    }

    // Detect numbered list block: every line starts with number + . or )
    if (lines.length > 0 && lines.every((l) => /^\d+[.)]\s/.test(l))) {
      const items = lines.map((l) => `<li>${escapeHtml(l.replace(/^\d+[.)]\s+/, ''))}</li>`);
      htmlParts.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    // Regular paragraph: collapse single newlines to spaces
    const paragraph = lines.join(' ');
    htmlParts.push(`<p>${escapeHtml(paragraph)}</p>`);
  }

  return htmlParts.join('');
}

// ── Helpers ────────────────────────────────────────────────────────────────

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Down-map heading levels:
 *  H1 → H{MIN_HEADING}
 *  H4, H5, H6 → H{MAX_HEADING}
 */
function downMapHeadings(html: string): string {
  return html.replace(/<(\/?)h([1-6])(\s[^>]*)?>/gi, (_match, slash: string, level: string, attrs: string) => {
    let n = parseInt(level, 10);
    if (n < MIN_HEADING) {n = MIN_HEADING;}
    if (n > MAX_HEADING) {n = MAX_HEADING;}
    return `<${slash}h${n}${attrs ?? ''}>`;
  });
}

/**
 * Convert Word's `<p class="MsoListParagraph">` runs into real `<ul>/<ol>`.
 *
 * Word emits lists as sequences of <p> elements with class MsoListParagraph.
 * Each paragraph contains the bullet/number character inside a <span> with
 * `mso-list: Ignore` style, followed by the list item text.
 *
 * We detect these sequences and rebuild them as proper HTML lists.
 */
function convertMsoListsToHTML(html: string): string {
  // Use a simple DOM parser approach via regex since we're in a browser-less
  // transform context. We look for consecutive MsoListParagraph paragraphs.

  // Match individual MsoListParagraph paragraphs
  const listParaRegex = /<p[^>]*class=["'][^"']*MsoListParagraph[^"']*["'][^>]*>([\s\S]*?)<\/p>/gi;

  // Collect all matches with their positions
  const matches: Array<{ fullMatch: string; content: string; index: number }> = [];
  let match: RegExpExecArray | null;

  while ((match = listParaRegex.exec(html)) !== null) {
    matches.push({
      fullMatch: match[0],
      content: match[1],
      index: match.index,
    });
  }

  if (matches.length === 0) {return html;}

  // Build replacement list HTML from consecutive matches
  let result = html;
  // Process from end to start so indices stay valid
  const groups = groupConsecutiveMatches(matches, result);

  for (let i = groups.length - 1; i >= 0; i--) {
    const group = groups[i];
    const items = group.map((m) => {
      // Strip the leading bullet/number glyph span
      let content = m.content;
      // Remove <span style="mso-list:Ignore">...</span> (the bullet/number)
      content = content.replace(/<span[^>]*mso-list:\s*Ignore[^>]*>[\s\S]*?<\/span>/gi, '');
      // Clean up remaining spans/tags
      content = content.replace(/<\/?span[^>]*>/gi, '');
      content = content.trim();
      return `<li>${content}</li>`;
    });

    // Detect if this is an ordered list by checking if the first item's
    // original content started with a number glyph
    const firstContent = group[0].content;
    const isOrdered = /^\s*(<span[^>]*>)?\s*\d/.test(firstContent);
    const tag = isOrdered ? 'ol' : 'ul';
    const listHtml = `<${tag}>${items.join('')}</${tag}>`;

    // Replace the entire run of matched paragraphs
    const startIdx = group[0].index;
    const lastMatch = group[group.length - 1];
    const endIdx = lastMatch.index + lastMatch.fullMatch.length;
    result = result.slice(0, startIdx) + listHtml + result.slice(endIdx);
  }

  return result;
}

/**
 * Group consecutive MsoListParagraph matches (allowing only whitespace between them).
 */
function groupConsecutiveMatches(
  matches: Array<{ fullMatch: string; content: string; index: number }>,
  html: string
): Array<Array<{ fullMatch: string; content: string; index: number }>> {
  if (matches.length === 0) {return [];}

  const groups: Array<Array<{ fullMatch: string; content: string; index: number }>> = [];
  let currentGroup = [matches[0]];

  for (let i = 1; i < matches.length; i++) {
    const prevEnd = matches[i - 1].index + matches[i - 1].fullMatch.length;
    const gap = html.slice(prevEnd, matches[i].index);
    // If only whitespace between, they're in the same group
    if (/^\s*$/.test(gap)) {
      currentGroup.push(matches[i]);
    } else {
      groups.push(currentGroup);
      currentGroup = [matches[i]];
    }
  }
  groups.push(currentGroup);

  return groups;
}

/** Normalise smart quotes and non-breaking spaces */
function normaliseTypography(html: string): string {
  return html
    .replace(/[\u201C\u201D]/g, '"')  // Smart double quotes → straight
    .replace(/[\u2018\u2019]/g, "'")   // Smart single quotes → straight
    .replace(/\u00A0/g, ' ');          // Non-breaking space → normal space
}
